import { Fragment, useEffect, useState, useContext } from 'react';
import { Box, Grid, Meter, Text, ResponsiveContext, ThemeContext } from 'grommet';
import { Calendar } from '@hpe-design/icons-grommet';
import { TextEmphasis, defaultWindow, DEMO_DATE, REPORT_WINDOW_MAP } from '../../helpers';
import { ChartCard, Measure, MeterGroup } from '../../templates/dashboards';
import { getGlassStyle } from '../../helpers/GlassStyles';

const MOCK_DATA = require('./mockdata/compliance.json');

export const RulesAudit = ({ period }) => {
  const { audits, checks } = MOCK_DATA;
  const size = useContext(ResponsiveContext);
  const isMobile = ['xsmall', 'small'].includes(size);
  const theme = useContext(ThemeContext);
  const isDarkMode = theme.dark;

  const glass = getGlassStyle(isDarkMode, 1.015);

  const [reportWindow, setReportWindow] = useState(defaultWindow);
  const [completedAudits, setCompletedAudits] = useState(null);
  const [nextAudit, setNextAudit] = useState(null);
  const [rules, setRules] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [auditResults, setAuditResults] = useState([]);

  useEffect(() => {
    setReportWindow(prevWindow => ({
      ...prevWindow,
      begin: new Date(new Date(DEMO_DATE).setDate(prevWindow.end.getDate() - REPORT_WINDOW_MAP[period])),
    }));
  }, [period]);

  useEffect(() => {
    const completed = audits
      .filter(audit => audit.completedDate)
      .sort((a, b) => new Date(b.completedDate) - new Date(a.completedDate))
      .filter(audit => new Date(audit.completedDate) < reportWindow.end && new Date(audit.completedDate) > reportWindow.begin);
    const next = audits
      .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate))
      .find(audit => new Date(audit.scheduledDate) > reportWindow.end);
    setCompletedAudits(completed);
    setNextAudit(next);
  }, [audits, reportWindow.begin, reportWindow.end]);

  useEffect(() => {
    if (completedAudits) {
      const nextRules = checks.filter(check => completedAudits.map(audit => audit.id).includes(check.auditId));
      const nextFrameworks = [...new Set(checks.map(check => check.frameworkId))];
      setRules(nextRules);
      setFrameworks(nextFrameworks);
    }
  }, [checks, completedAudits]);

  useEffect(() => {
    const nextAuditResults = [
      { result: 'pass', count: 0, percent: 0, color: 'status-ok' },
      { result: 'fail', count: 0, percent: 0, color: 'status-critical' },
      { result: 'error', count: 0, percent: 0, color: 'status-warning' },
    ];
    rules.forEach(rule => {
      const item = nextAuditResults.find(el => el.result === rule.result);
      if (item) {
        item.count++;
        item.percent = (item.count / rules.length) * 100;
      }
    });
    setAuditResults(nextAuditResults);
  }, [rules]);

  const gridConfig = isMobile
    ? {
        columns: ['1fr', '1fr'],
        rows: ['auto', 'auto', 'auto'],
        areas: [
          ['rules', 'frameworks'],
          ['audits', 'audits'],
          ['chart', 'chart'],
        ],
        gap: 'small',
      }
    : {
        columns: ['auto', 'auto', 'auto'],
        rows: ['auto', 'auto'],
        areas: [
          ['rules', 'frameworks', 'audits'],
          ['chart', 'chart', 'chart'],
        ],
        gap: 'medium',
      };

  return (
// RulesAudit.js - Update the ChartCard return block
<ChartCard
  title="Health Checks"
  subtitle="Global Monitoring"
  elevation="none"
  height={isMobile ? 'auto' : { min: '384px' }}
  /* 1. Add e to the argument and blur the target */
  onClick={(e) => {
    if (e.currentTarget) e.currentTarget.blur();
    window.dispatchEvent(new CustomEvent('show-coming-soon'));
  }}
  /* 2. Disable the Grommet focus ring */
  focusIndicator={false}
  footer={nextAudit && (
    <Box direction="row" gap="xsmall" align="center">
      <Calendar size="small" color="text-strong" />
      <TextEmphasis size="small" color="text-strong">
        Next audit in 5 days.
      </TextEmphasis>
    </Box>
  )}
  /* 3. Ensure no persistent outlines or shadows via style */
  style={{ 
    ...glass.container,
    outline: 'none',
    boxShadow: 'none'
  }}
  onMouseEnter={glass.hover}
  onMouseLeave={glass.leave}
>
      <Box flex="grow" justify="start">
        <Grid columns={gridConfig.columns} rows={gridConfig.rows} areas={gridConfig.areas} gap={gridConfig.gap}>
          <Measure gridArea="rules" name={{ label: { label: 'Rules' } }} value={rules?.length} />
          <Measure gridArea="frameworks" name={{ label: { label: 'Frameworks' } }} value={frameworks?.length} />
          <Measure gridArea="audits" name={{ label: { label: 'Audits' } }} value={completedAudits?.length} />
          <Box gridArea="chart" margin={{ top: isMobile ? 'medium' : 'small' }}>
            <AuditResultsChart data={auditResults} />
          </Box>
        </Grid>
      </Box>
    </ChartCard>
  );
};

const AuditResultsChart = ({ data, ...rest }) => (
  <MeterGroup {...rest}>
    {data.map(datum => (
      <Fragment key={datum.result}>
        <Text size="small" color="text-strong" weight="bold">
          {datum.result.charAt(0).toUpperCase() + datum.result.slice(1)}
        </Text>
        <Meter type="bar" values={[{ value: datum.percent, color: datum.color }]} thickness="small" />
        <Box align="end">
          <Text size="small" color="text-strong" weight="bold">{datum.count}</Text>
        </Box>
      </Fragment>
    ))}
  </MeterGroup>
);
